import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import HandTracker from './HandTracker';
import DrawingCanvas from './DrawingCanvas';
import { OneEuroFilter2D } from './SmoothingUtils';

function App() {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState<'brush' | 'eraser' | 'marker' | 'spray'>('brush');
  const [currentColor, setCurrentColor] = useState('#FF6B6B');
  const [brushSize, setBrushSize] = useState(5);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownValue, setCountdownValue] = useState(3);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [showCalibration, setShowCalibration] = useState(false);
  const [pinchThreshold, setPinchThreshold] = useState(0.05);
  const [smoothingBeta, setSmoothingBeta] = useState(0.05);
  const [resolutionScale, setResolutionScale] = useState(0.5); // 50% by default for performance

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const handTrackerRef = useRef<HandTracker | null>(null);
  const drawingCanvasRef = useRef<DrawingCanvas | null>(null);
  const animationFrameRef = useRef<number>(0);
  const isDrawingRef = useRef<boolean>(false);
  const pointerFilterRef = useRef<OneEuroFilter2D | null>(null);
  const lastPinchClosedRef = useRef<boolean>(false);
  const lastUIClickTimeRef = useRef<number>(0);
  const pinchThresholdRef = useRef<number>(0.05);
  const smoothingBetaRef = useRef<number>(0.05);
  const lastHoverElRef = useRef<HTMLElement | null>(null);
  const palmRingRef = useRef<HTMLDivElement | null>(null);
  const palmEraseEnabledRef = useRef<boolean>(true);
  const palmRadiusRef = useRef<number>(60);

  const colorPalette = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'];

  const initCamera = useCallback(async () => {
    // Clean up existing stream before initializing
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    try {
      const baseWidth = 640;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: baseWidth * resolutionScale },
          height: { ideal: (baseWidth * 0.75) * resolutionScale },
          frameRate: { ideal: 24, max: 30 }
        }
      });
      if (videoRef.current) {
        streamRef.current = stream;
        videoRef.current.srcObject = stream;
        videoRef.current.onloadeddata = async () => {
          if (videoRef.current) {
            const video = videoRef.current;
            const videoWidth = video.videoWidth;
            const videoHeight = video.videoHeight;
            if (canvasRef.current) {
              // Initialize or update drawing canvas dimensions
              if (!drawingCanvasRef.current) {
                drawingCanvasRef.current = new DrawingCanvas(canvasRef.current);
              }
              drawingCanvasRef.current.setDimensions(videoWidth, videoHeight);
            }
            // Initialize tracker if it's not already there
            if (!handTrackerRef.current) {
              pointerFilterRef.current = new OneEuroFilter2D(60, 1.0, smoothingBetaRef.current, 1.0);
              handTrackerRef.current = new HandTracker(handleHandsDetected);
              try {
                setIsModelLoading(true);
                await handTrackerRef.current!.initialize(videoRef.current!);
              } catch (err) {
                console.error('Model initialization failed:', err);
                alert('Hand tracking failed to initialize. Please try again or use touch mode.');
              } finally {
                setIsModelLoading(false);
              }
              startTrackingLoop();
            }
          }
        };
        videoRef.current.play();
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Unable to access camera. Please check permissions.');
    }
  }, [resolutionScale]);

  useEffect(() => {
    if (isCameraActive) initCamera();
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (handTrackerRef.current) handTrackerRef.current.destroy();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isCameraActive, initCamera]);

  // Effect to handle resizing the canvas when video dimensions change
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleResize = () => {
      if (drawingCanvasRef.current && video.videoWidth > 0) {
        drawingCanvasRef.current.setDimensions(video.videoWidth, video.videoHeight);
      }
    };
    video.addEventListener('resize', handleResize);
    return () => video.removeEventListener('resize', handleResize);
  }, []);

  const startTrackingLoop = () => {
    const track = () => {
      if (handTrackerRef.current && videoRef.current) handTrackerRef.current.update();
      animationFrameRef.current = requestAnimationFrame(track);
    };
    animationFrameRef.current = requestAnimationFrame(track);
  };

  const handleHandsDetected = (hands: any[]) => {
    if (isModelLoading) return;
    if (!hands || hands.length === 0) {
      if (isDrawingRef.current) {
        isDrawingRef.current = false;
        setIsDrawing(false);
        drawingCanvasRef.current?.setDrawingState(false);
      }
      if (cursorRef.current) cursorRef.current.dataset.emoji = '';
      lastPinchClosedRef.current = false;
      if (lastHoverElRef.current) {
        lastHoverElRef.current.classList.remove('ai-hover');
        lastHoverElRef.current = null;
      }
      if (palmRingRef.current) palmRingRef.current.style.display = 'none';
      return;
    }

    const primary = hands.find((h: any) => h.handedness === 'Right') || hands[0];
    const secondary = hands.find((h: any) => h !== primary);
    [primary, secondary].filter(Boolean).forEach((hand: any, idx: number) => {
      const pinchDistance = handTrackerRef.current?.calculatePinchDistance(hand);
      if (pinchDistance == null) return;

      // Draw strictly on pinch midpoint like the working sample
      const pinchMid = handTrackerRef.current?.getPinchMidpoint(hand);
      if (!pinchMid || !videoRef.current || !cursorRef.current || !canvasRef.current) return;

      const container = videoRef.current.parentElement as HTMLElement;
      const rect = container.getBoundingClientRect();
      const appEl = appRef.current || container.parentElement as HTMLElement;
      const appRect = appEl.getBoundingClientRect();
      const video = videoRef.current;
      const videoW = video.videoWidth || 1;
      const videoH = video.videoHeight || 1;
      const rV = videoW / videoH;
      const rC = rect.width / rect.height;

      let nx = 1 - pinchMid.x;
      let ny = pinchMid.y;
      let x = 0;
      let y = 0;
      if (rC > rV) {
        const scaledH = rect.width / rV;
        const offsetY = (scaledH - rect.height) / 2;
        x = nx * rect.width;
        y = ny * scaledH - offsetY;
      } else {
        const scaledW = rect.height * rV;
        const offsetX = (scaledW - rect.width) / 2;
        x = nx * scaledW - offsetX;
        y = ny * rect.height;
      }
      // One Euro smoothing on pixel coords
      const filtered = pointerFilterRef.current
        ? pointerFilterRef.current.filter({ x, y }, performance.now())
        : { x, y };
      x = Math.max(0, Math.min(rect.width, filtered.x));
      y = Math.max(0, Math.min(rect.height, filtered.y));

      // Map to app-container coordinates for cursor and UI hit-testing
      const viewportX = rect.left + x;
      const viewportY = rect.top + y;
      const appX = viewportX - appRect.left;
      const appY = viewportY - appRect.top;

      cursorRef.current.style.left = `${appX}px`;
      cursorRef.current.style.top = `${appY}px`;
      cursorRef.current.dataset.emoji = '';

      // ====================================================================================
      // START: Palm Eraser Feature (Disabled)
      // The following code block implements the palm eraser functionality.
      // It detects an open palm gesture and uses it to erase parts of the drawing.
      // To re-enable, uncomment this entire block.
      /*
      // Palm eraser: heuristic using MCPs, fingertips, wrist, and middle fingertip direction
      const mcpIndices = [5, 9, 13, 17]; // MCPs
      const tipIndices = [8, 12, 16, 20]; // fingertips
      const wristIndex = 0; // wrist
      const middleTipIndex = 12; // middle fingertip for direction
      const points = mcpIndices
        .map((i) => handTrackerRef.current?.getLandmarkPosition(hand, i))
        .filter(Boolean) as { x: number; y: number }[];
      const tips = tipIndices
        .map((i) => handTrackerRef.current?.getLandmarkPosition(hand, i))
        .filter(Boolean) as { x: number; y: number }[];
      const wrist = handTrackerRef.current?.getLandmarkPosition(hand, wristIndex) as { x: number; y: number } | null;
      const middleTip = handTrackerRef.current?.getLandmarkPosition(hand, middleTipIndex) as { x: number; y: number } | null;
      let palmActive = false;
      let palmCenterX = x;
      let palmCenterY = y;
      let palmLengthEstimatePx = 0;
      if (points.length === 4 && tips.length === 4 && wrist && middleTip) {
        // Compute average distance from center as a radius proxy
        const cx = (points[0].x + points[1].x + points[2].x + points[3].x) / 4;
        const cy = (points[0].y + points[1].y + points[2].y + points[3].y) / 4;
        // Fingertip distances (if they are far from center and MCPs spread -> palm likely open)
        const tipSpread = tips.reduce((acc, p) => acc + Math.hypot(p.x - cx, p.y - cy), 0) / 4;
        // Map center to pixel coords similar to pinchMid mapping
        let px = 0; let py = 0;
        if (rC > rV) {
          const scaledH = rect.width / rV;
          const offsetY = (scaledH - rect.height) / 2;
          px = (1 - cx) * rect.width; // mirror X
          py = cy * scaledH - offsetY;
        } else {
          const scaledW = rect.height * rV;
          const offsetX = (scaledW - rect.width) / 2;
          px = (1 - cx) * scaledW - offsetX;
          py = cy * rect.height;
        }
        // Project eraser circle from middle fingertip towards wrist to cover palm longitudinally
        // Compute vector middleTip -> wrist in normalized coords
        const vx = wrist.x - middleTip.x;
        const vy = wrist.y - middleTip.y;
        const vLen = Math.hypot(vx, vy) || 1;
        const dirX = vx / vLen;
        const dirY = vy / vLen;
        // Map middleTip to pixel coords
        let mtX = 0; let mtY = 0;
        if (rC > rV) {
          const scaledH = rect.width / rV;
          const offsetY = (scaledH - rect.height) / 2;
          mtX = (1 - middleTip.x) * rect.width;
          mtY = middleTip.y * scaledH - offsetY;
        } else {
          const scaledW = rect.height * rV;
          const offsetX = (scaledW - rect.width) / 2;
          mtX = (1 - middleTip.x) * scaledW - offsetX;
          mtY = middleTip.y * rect.height;
        }
        // Eraser center: a bit پایین‌تر از نوک انگشت وسط به سمت مچ
        const palmLengthPx = Math.hypot(px - mtX, py - mtY);
        palmLengthEstimatePx = palmLengthPx;
        const centerOffset = Math.min(0.4, 0.25 + (palmLengthPx / Math.max(rect.width, rect.height)) * 0.2);
        palmCenterX = mtX + dirX * (palmLengthPx * centerOffset);
        palmCenterY = mtY + dirY * (palmLengthPx * centerOffset);
        const mcpSpread = points.reduce((acc, p) => acc + Math.hypot(p.x - cx, p.y - cy), 0) / 4;
        // Stronger condition: both spreads high and pinch clearly open
        palmActive = (mcpSpread > 0.05 && tipSpread > 0.12) && pinchDistance > (pinchThresholdRef.current + 0.02);
      }
      if (palmActive && palmEraseEnabledRef.current) {
        // Show ring at palm center
        const viewportPalmX = rect.left + palmCenterX;
        const viewportPalmY = rect.top + palmCenterY;
        const appPalmX = viewportPalmX - appRect.left;
        const appPalmY = viewportPalmY - appRect.top;
        // Radius proportional to palm length (from middle tip to wrist)
        const radius = Math.max(50, Math.min(160, (palmLengthEstimatePx || 120) * 0.55));
        if (!palmRingRef.current) {
          const el = document.createElement('div');
          el.className = 'palm-ring';
          document.body.appendChild(el);
          palmRingRef.current = el;
        }
        const ring = palmRingRef.current!;
        ring.style.display = 'block';
        ring.style.width = `${radius * 2}px`;
        ring.style.height = `${radius * 2}px`;
        ring.style.left = `${appRect.left + appPalmX - radius}px`;
        ring.style.top = `${appRect.top + appPalmY - radius}px`;
        // Erase on canvas under palm
        drawingCanvasRef.current?.eraseCircleAt(palmCenterX, palmCenterY, radius);
        // Suppress drawing while palm active
        if (isDrawingRef.current) {
          isDrawingRef.current = false;
          setIsDrawing(false);
          drawingCanvasRef.current?.setDrawingState(false);
        }
        return; // skip pinch drawing if palm erasing
      } else if (palmRingRef.current) {
        palmRingRef.current.style.display = 'none';
      }
      */
      // END: Palm Eraser Feature (Disabled)
      // ====================================================================================

      // Pinch gating: draw vs UI click (threshold is calibratable)
      const isClosed = pinchDistance < pinchThresholdRef.current;

      // Only let primary hand (idx===0) interact with UI to avoid double clicks
      if (idx === 0) {
        // Probe multiple depths to be robust over transforms and containers
        const targetAt = (x: number, y: number) => document.elementFromPoint(x, y) as HTMLElement | null;
        const targets = [
          targetAt(viewportX, viewportY),
          targetAt(viewportX, viewportY - 1),
          targetAt(viewportX - 1, viewportY),
          targetAt(viewportX + 1, viewportY),
          targetAt(viewportX, viewportY + 1),
        ].filter(Boolean) as HTMLElement[];
        let clickableEl: HTMLElement | null = null;
        for (const t of targets) {
          const c = getClickable(t);
          if (c) { clickableEl = c; break; }
        }
        const isUI = !!clickableEl;
        // Hover highlight
        if (clickableEl !== lastHoverElRef.current) {
          if (lastHoverElRef.current) lastHoverElRef.current.classList.remove('ai-hover');
          if (clickableEl) clickableEl.classList.add('ai-hover');
          lastHoverElRef.current = clickableEl;
        }
        if (isClosed && isUI) {
          // Rising edge: trigger click once
          if (!lastPinchClosedRef.current) {
            const now = performance.now();
            if (now - lastUIClickTimeRef.current > 300) {
              simulateClick(clickableEl!);
              lastUIClickTimeRef.current = now;
            }
          }
        }
        // If hovering UI, suppress drawing
        if (isUI) {
          // Update pinch state and return without drawing
          lastPinchClosedRef.current = isClosed;
          if (isDrawingRef.current) {
            isDrawingRef.current = false;
            setIsDrawing(false);
            drawingCanvasRef.current?.setDrawingState(false);
          }
          return;
        }
        if (!isUI && lastHoverElRef.current) {
          lastHoverElRef.current.classList.remove('ai-hover');
          lastHoverElRef.current = null;
        }
      }

      if (isClosed) {
        if (!isDrawingRef.current) {
          isDrawingRef.current = true;
          setIsDrawing(true);
          drawingCanvasRef.current?.setDrawingState(true);
        }
        if (drawingCanvasRef.current) {
          drawingCanvasRef.current.drawAt(x, y);
        }
      } else if (isDrawingRef.current) {
        isDrawingRef.current = false;
        setIsDrawing(false);
        drawingCanvasRef.current?.setDrawingState(false);
      }

      if (idx === 0) {
        lastPinchClosedRef.current = isClosed;
      }
    });
  };

  const startScreenshot = () => {
    setShowCountdown(true);
    setCountdownValue(3);
    const timer = setInterval(() => {
      setCountdownValue((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setTimeout(() => {
            takeScreenshot();
            setShowCountdown(false);
          }, 400);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const takeScreenshot = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const out = document.createElement('canvas');
    const ctx = out.getContext('2d');
    if (!ctx) return;
    out.width = video.videoWidth;
    out.height = video.videoHeight;

    // Draw mirrored video and drawing (to match on-screen selfie preview)
    ctx.save();
    ctx.translate(out.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, out.width, out.height);
    if (drawingCanvasRef.current) {
      const drawingCanvas = drawingCanvasRef.current.getCanvas();
      ctx.drawImage(drawingCanvas, 0, 0, out.width, out.height);
    }
    ctx.restore();

    const label = 'pinch-to-draw.mmetehrani.ir';
    const iconSrc = (process.env.PUBLIC_URL || '') + '/icon-192.png';

    const finalize = (icon?: HTMLImageElement) => {
      ctx.font = '14px Arial';
      const textMetrics = ctx.measureText(label);
      const textW = Math.ceil(textMetrics.width);
      const iconW = icon ? 22 : 0;
      const iconH = icon ? 22 : 0;
      const gap = icon ? 8 : 0;
      const pad = 10;
      const boxH = 28;
      const boxW = Math.max(200, iconW + gap + textW + 16);
      const boxX = out.width - (boxW + pad);
      const boxY = out.height - (boxH + pad);
      // Background pill
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(boxX, boxY, boxW, boxH);
      // Icon (left side)
      if (icon) {
        ctx.drawImage(icon, boxX + 6, boxY + (boxH - iconH) / 2, iconW, iconH);
      }
      // Text
      ctx.fillStyle = '#fff';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      const textX = boxX + 6 + (icon ? iconW + gap : 0);
      const textY = boxY + boxH / 2;
      ctx.fillText(label, textX, textY);

      out.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `pinch-to-draw-${new Date().toISOString().slice(0, 19)}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      });
    };

    const icon = new Image();
    icon.onload = () => finalize(icon);
    icon.onerror = () => finalize();
    icon.src = iconSrc;
  };

  const clearCanvas = () => drawingCanvasRef.current?.clear();
  const undoLastAction = () => drawingCanvasRef.current?.undo();

  useEffect(() => {
    if (drawingCanvasRef.current) drawingCanvasRef.current.setCurrentColor(currentColor);
  }, [currentColor]);
  useEffect(() => {
    if (drawingCanvasRef.current) drawingCanvasRef.current.setCurrentSize(brushSize);
  }, [brushSize]);
  useEffect(() => {
    if (drawingCanvasRef.current) drawingCanvasRef.current.setCurrentTool(currentTool);
  }, [currentTool]);

  return (
    <div className="app-container" ref={appRef}>
      <div className="video-container">
        <video ref={videoRef} autoPlay playsInline muted style={{ display: isCameraActive ? 'block' : 'none' }} />
        <canvas ref={canvasRef} className="drawing-canvas" />
      </div>

      {isModelLoading && (
        <div className="loading-overlay">
          <div className="content">
            <div className="spinner" />
            <div>در حال بارگذاری مدل دست...</div>
          </div>
        </div>
      )}

      {showCountdown && (
        <div className="countdown-overlay">
          <div className="countdown-text">{countdownValue}</div>
        </div>
      )}

      {showOnboarding && (
        <div className="onboarding-screen">
          <div className="onboarding-card">
            <h2>Pinch-to-Draw</h2>
            <div className="onboarding-steps">
              <div className="step">
                <div className="step-number">1</div>
                <p>Allow camera access to start</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <p>Bring thumb and index finger together to draw</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <p>Press save button to download your artwork</p>
              </div>
            </div>
            <button
              className="start-btn"
              onClick={() => {
                setIsCameraActive(true);
                setShowOnboarding(false);
              }}
              title="Start"
            >
              Start Drawing
            </button>
          </div>
        </div>
      )}

      {isCameraActive && !isModelLoading && (
      <div className="toolbar">
        <div className="toolbar-inner">
        <button className="tool-btn" onClick={undoLastAction} title="Undo" aria-label="Undo">
          ⤺
        </button>
        <button className="tool-btn" onClick={clearCanvas} title="Clear" aria-label="Clear">
          🗑️
        </button>
        <div className="size-presets">
          {[4, 8, 14, 22].map((size) => (
            <button
              key={size}
              className={`size-btn ${brushSize === size ? 'active' : ''}`}
              onClick={() => setBrushSize(size)}
              title={`Size ${size}`}
              aria-label={`Brush size ${size}`}
            >
              <span className="dot" style={{ width: size / 2, height: size / 2, display: 'inline-block' }} />
            </button>
          ))}
        </div>
        <div className="color-palette">
          {colorPalette.map((color) => (
            <button
              key={color}
              className={`color-btn ${currentColor === color ? 'active' : ''}`}
              style={{ backgroundColor: color }}
              onClick={() => setCurrentColor(color)}
              title={`Color: ${color}`}
              aria-label={`Color: ${color}`}
            />
          ))}
        </div>
        <button
          className={`tool-btn ${currentTool === 'brush' ? 'active' : ''}`}
          onClick={() => setCurrentTool('brush')}
          title="Brush"
          aria-label="Brush"
        >
          🖌️
        </button>
        <button
          className={`tool-btn ${currentTool === 'marker' ? 'active' : ''}`}
          onClick={() => setCurrentTool('marker')}
          title="Marker"
          aria-label="Marker"
        >
          🖊️
        </button>
        <button
          className={`tool-btn ${currentTool === 'spray' ? 'active' : ''}`}
          onClick={() => setCurrentTool('spray')}
          title="Spray"
          aria-label="Spray"
        >
          💨
        </button>
        <button
          className={`tool-btn ${currentTool === 'eraser' ? 'active' : ''}`}
          onClick={() => setCurrentTool('eraser')}
          title="Eraser"
          aria-label="Eraser"
        >
          🧽
        </button>
        <button className="tool-btn" onClick={() => setShowCalibration((v) => !v)} title="Calibration" aria-label="Calibration">⚙️</button>
        <div className="resolution-control">
          <button
            className={`tool-btn ${resolutionScale === 0.5 ? 'active' : ''}`}
            onClick={() => setResolutionScale(0.5)}
            title="50% Resolution"
          >
            SD
          </button>
          <button
            className={`tool-btn ${resolutionScale === 1.0 ? 'active' : ''}`}
            onClick={() => setResolutionScale(1.0)}
            title="100% Resolution"
          >
            HD
          </button>
        </div>
        <button className="tool-btn save-btn" onClick={startScreenshot} title="Save" aria-label="Save">📸</button>
        </div>
      </div>
      )}

      {/* Global cursor overlay in app space */}
      <div
        ref={cursorRef}
        className={`cursor ${isDrawing ? 'drawing' : ''}`}
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: `${brushSize * (isDrawing ? 1.5 : 1)}px`,
          height: `${brushSize * (isDrawing ? 1.5 : 1)}px`,
          borderRadius: '50%',
          backgroundColor: isDrawing ? currentColor : 'transparent',
          border: isDrawing ? 'none' : `2px solid ${currentColor}`,
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          transition: 'all 0.1s ease',
          zIndex: 10,
        }}
      />

      {/* Calibration overlay */}
      {showCalibration && (
        <div className="calibration-overlay" onClick={() => setShowCalibration(false)}>
          <div className="calibration-card" onClick={(e) => e.stopPropagation()}>
            <h2>Calibration</h2>
            <p>Adjust pinch sensitivity and smoothing.</p>
            <div className="calibration-progress" style={{ height: 10 }}>
              <div className="progress-bar" style={{ width: `${Math.min(100, Math.max(0, (1 - (pinchThreshold - 0.02) / 0.1)) * 100)}%` }} />
            </div>
            <div style={{ margin: '10px 0', textAlign: 'left' }}>
              <label>Pinch sensitivity</label>
              <input
                type="range"
                min="0.02"
                max="0.12"
                step="0.005"
                value={pinchThreshold}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setPinchThreshold(v);
                  pinchThresholdRef.current = v;
                }}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ margin: '10px 0', textAlign: 'left' }}>
              <label>Smoothing (beta)</label>
              <input
                type="range"
                min="0"
                max="0.2"
                step="0.01"
                value={smoothingBeta}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setSmoothingBeta(v);
                  smoothingBetaRef.current = v;
                  pointerFilterRef.current = new OneEuroFilter2D(60, 1.0, v, 1.0);
                }}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;

// Helper: detect interactive UI elements we want to click
function isInteractiveElement(el: HTMLElement): boolean {
  if (!el) return false;
  if (el.closest('.onboarding-screen .start-btn')) return true;
  if (el.closest('.toolbar .tool-btn')) return true;
  if (el.closest('.toolbar .color-btn')) return true;
  if (el.closest('.toolbar .size-btn')) return true;
  return false;
}

function simulateClick(el: HTMLElement) {
  // Prefer native click to trigger React handlers
  (el as HTMLButtonElement).click?.();
}

function getClickable(el: HTMLElement): HTMLElement | null {
  return (
    el.closest('.onboarding-screen .start-btn') ||
    el.closest('.toolbar .tool-btn') ||
    el.closest('.toolbar .color-btn') ||
    el.closest('.toolbar .size-btn')
  );
}
